/*
 * Saneado de eventos antes de que salgan hacia Sentry.
 *
 * Compartido por las tres inicializaciones (cliente, servidor y edge). El
 * navegador es el entorno más expuesto de los tres: además del cuerpo de las
 * peticiones arrastra migas de pan de `fetch` y de `console`, y en esta
 * aplicación una miga de `console.error` puede llevar la respuesta entera del
 * backend, es decir material clínico.
 */

/*
 * Vista interna, laxa, de los campos que se limpian. Las firmas públicas usan
 * `T extends object` porque los tipos de Sentry (`ErrorEvent`,
 * `TransactionEvent`, `Log`) no llevan índice de cadena y no encajarían en un
 * `Record<string, unknown>`; la conversión se hace sólo puertas adentro.
 */
type Evento = {
  request?: Record<string, unknown>;
  extra?: unknown;
  contexts?: Record<string, unknown>;
  breadcrumbs?: Array<Record<string, unknown>>;
};

// Claves cuyo valor nunca debe abandonar el navegador. Cubre credenciales y
// datos clínicos: aquí un `texto` o un `mensaje` es transcripción de terapia.
const CLAVES_SENSIBLES = [
  'password', 'contrasena', 'contraseña', 'token', 'authorization', 'cookie',
  'secret', 'apikey', 'api_key', 'transcripcion', 'texto', 'mensaje',
  'message', 'contenido', 'notas', 'resumen', 'pregunta', 'respuesta',
  'nombre', 'apellido', 'email', 'dni', 'telefono', 'diagnostico'
];

const MARCADOR = '[REDACTADO]';

function esSensible(clave: string): boolean {
  const normalizada = clave.toLowerCase();
  return CLAVES_SENSIBLES.some(sensible => normalizada.includes(sensible));
}

/**
 * Sustituye los valores sensibles conservando la forma del objeto. Saber que
 * venía un campo `transcripcion` ayuda a depurar; su contenido, no.
 */
export function redactar(valor: unknown, profundidad = 0): unknown {
  if (profundidad > 4 || valor === null || typeof valor !== 'object') return valor;
  if (Array.isArray(valor)) return valor.map(v => redactar(v, profundidad + 1));

  return Object.fromEntries(
    Object.entries(valor as Record<string, unknown>).map(([clave, v]) => [
      clave,
      esSensible(clave) ? MARCADOR : redactar(v, profundidad + 1)
    ])
  );
}

/** Conserva la ruta y descarta la query, que aquí lleva términos clínicos. */
export function urlSinQuery(url: unknown): unknown {
  return typeof url === 'string' ? url.split('?')[0] : url;
}

/**
 * Las migas de pan son el mayor vector del navegador: `fetch` guarda la URL
 * completa y `console` guarda los argumentos con los que se llamó.
 */
function sanearMigas(migas: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
  return migas.map(miga => {
    const datos = miga.data as Record<string, unknown> | undefined;
    const limpia: Record<string, unknown> = { ...miga };

    if (datos) {
      limpia.data = redactar({ ...datos, url: urlSinQuery(datos.url) });
    }
    // El mensaje de una miga de consola es texto libre ya interpolado: no hay
    // forma de redactarlo por clave, así que se descarta el cuerpo y se
    // conserva sólo que hubo un log de ese nivel.
    if (miga.category === 'console') {
      limpia.message = MARCADOR;
    }
    return limpia;
  });
}

/**
 * Limpia un evento de error o de transacción. `sendDefaultPii: false` no basta
 * por sí solo: las transacciones no pasan por `beforeSend` sino por
 * `beforeSendTransaction`, y ninguna de las dos toca las migas de pan.
 */
export function sanearEvento<T extends object>(evento: T): T {
  const vista = evento as Evento;

  if (vista.request) {
    delete vista.request.data;
    delete vista.request.cookies;
    delete vista.request.query_string;
    vista.request.url = urlSinQuery(vista.request.url);
    if (vista.request.headers) {
      vista.request.headers = redactar(vista.request.headers) as Record<string, unknown>;
    }
  }

  const traza = vista.contexts?.trace as Record<string, unknown> | undefined;
  const http = traza?.data as Record<string, unknown> | undefined;
  if (http) {
    delete http['http.query'];
    http['http.url'] = urlSinQuery(http['http.url']);
    http['http.target'] = urlSinQuery(http['http.target']);
  }

  if (vista.breadcrumbs) vista.breadcrumbs = sanearMigas(vista.breadcrumbs);
  if (vista.extra) vista.extra = redactar(vista.extra);
  if (vista.contexts) vista.contexts = redactar(vista.contexts) as Record<string, unknown>;

  return evento;
}

/**
 * Los logs de consola viajan en su propio envelope y no pasan por `beforeSend`.
 * Se conserva el nivel y se descarta el cuerpo: sirve para ver que algo falla
 * con frecuencia, no para leer lo que falló.
 */
export function sanearLog<T extends object>(log: T): T {
  const vista = log as { attributes?: unknown };
  return { ...log, body: MARCADOR, attributes: redactar(vista.attributes) } as T;
}
